"use client"

import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode, useRef } from "react"
import type { 
  WorkflowItem,
  WorkflowStage,
  Filters, 
  WorkflowState, 
  WorkflowAction,
  User,
  WorkflowConfiguration,
  StageConfiguration,
  FieldConfiguration,
  AssignedUser,
  ClientCustomField
} from "@/types/workflow"
import type { SavedView, UserViewsResponse } from "@/types/saved-view"
import { LoadingState, FboEntityType } from "@/types/workflow"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useAuth } from "@/contexts/auth-context"
import { useToast } from "@/hooks/use-toast"
import { useCodeValues } from "@/hooks/use-code-values"
import { codeService } from "@/lib/code-service"
import { getFacetKeysFromConfiguration, getSelectFieldsFromConfiguration } from "@/utils/column-type-helpers"

// Simple debounce function
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
  let timeoutId: NodeJS.Timeout
  return ((...args: any[]) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }) as T
}

// Initial state
const initialState: WorkflowState = {
  // UI State
  currentStage: undefined,
  isFilterDrawerOpen: false,
  isBulkEditModalOpen: false,
  currentBulkEditField: null,
  
  // Data State
  workflowItems: [],
  columns: [],
  visibleColumns: [],
  filters: {},
  selectedItems: [],
  
  // Pagination State
  currentPage: 1,
  pageSize: 5,
  itemsPerPage: null, // null = auto, number = fixed page size
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  
  // Loading State
  loadingState: LoadingState.IDLE,
  
  // Error State
  error: null,
  
  // Views State
  savedViews: [],
  currentView: null,
  hasUnsavedChanges: false,
  
  // View data from API
  viewsResponse: null,
  
  // User State
  currentUser: {
    id: "1",
    name: "John Doe",
    count: 0,
    role: "Administrator",
    email: "john.doe@company.com"
  },

  // Configuration State
  configuration: undefined,
  isLoadingConfiguration: false,
  configurationError: null,

  // Stages
  stages: [],

  // Assigned Users State
  assignedUsers: [],
  isLoadingAssignedUsers: false,
  assignedUsersError: null,

  // Facets
  facets: undefined,

  // New state for stageFilters and globalFilters
  stageFilters: [],
  globalFilters: [],

  // New state for work orders facets
  workOrdersFacets: undefined,

  // Code Values State
  codeValuesData: undefined,
  isLoadingCodeValues: false,
  codeValuesError: null,

  // Column Widths State
  columnWidths: {},
  // Pinned columns per stage
  pinnedColumnsByStage: {},

  // Client Custom Fields State
  originalConfiguration: undefined,
  activeClientFboId: null,
  activeClientName: null,
  cachedClientCustomFields: new Map(),
  customFieldsApplied: false,
  isHandlingClientFilterChange: false,
}

// Reducer function
function workflowReducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
  switch (action.type) {
    case 'SET_CURRENT_STAGE':
      return { 
        ...state, 
        currentStage: action.payload,
        currentPage: 1, // Reset to first page when changing stage
        selectedItems: [], // Clear selections when changing stage
        filters: {}, // Clear filters when changing stage
        loadingState: LoadingState.LOADING_WORK_ORDERS // Set loading state immediately
      }
    
    case 'SET_LOADING_STATE':
      return { ...state, loadingState: action.payload }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loadingState: LoadingState.IDLE }
    
    case 'TOGGLE_FILTER_DRAWER':
      return { ...state, isFilterDrawerOpen: !state.isFilterDrawerOpen }
    
    case 'SET_WORK_ORDERS':
      // Force a new array reference to ensure React detects the change
      const newWorkflowItems = [...(action.payload.data || [])];
      return {
        ...state,
        workflowItems: newWorkflowItems,
        totalCount: action.payload.totalCount,
        hasNextPage: action.payload.hasNextPage,
        hasPreviousPage: action.payload.hasPreviousPage,
        loadingState: LoadingState.IDLE,
        error: null,
        workOrdersFacets: action.payload.facets ?? undefined
      }
    
    case 'SET_COLUMNS':
      return { 
        ...state, 
        columns: action.payload.columns,
        visibleColumns: action.payload.visibleColumns,
        // Don't reset loading state here - keep it until data is loaded
      }
    
    case 'UPDATE_VISIBLE_COLUMNS':
      return { 
        ...state, 
        visibleColumns: action.payload,
        hasUnsavedChanges: true
      }
    
    case 'UPDATE_FILTER':
      return { 
        ...state, 
        filters: { ...state.filters, [action.payload.key]: action.payload.value },
        currentPage: 1, // Reset to first page when filtering
        hasUnsavedChanges: true
      }

    
    case 'SET_SELECTED_ITEMS':
      return { ...state, selectedItems: action.payload }
    
    case 'TOGGLE_ITEM_SELECTION':
      const isSelected = state.selectedItems.includes(action.payload)
      return {
        ...state,
        selectedItems: isSelected
          ? state.selectedItems.filter(id => id !== action.payload)
          : [...state.selectedItems, action.payload]
      }
    
    case 'SELECT_ALL_ITEMS':
      return { ...state, selectedItems: action.payload }
    
    case 'DESELECT_ALL_ITEMS':
      return { ...state, selectedItems: [] }
    
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload }
    
    case 'SET_PAGE_SIZE':
      return { 
        ...state, 
        pageSize: action.payload,
        currentPage: 1 // Reset to first page when changing page size
      }
    
    case 'SET_ITEMS_PER_PAGE':
      return { 
        ...state, 
        itemsPerPage: action.payload,
        currentPage: 1 // Reset to first page when changing items per page
      }
    
    case 'UPDATE_WORK_ORDER_FIELD':
      const updatedItems = state.workflowItems.map(item =>
        item.id === action.payload.orderId
          ? { ...item, [action.payload.field]: action.payload.value }
          : item
      );
      
      return {
        ...state,
        workflowItems: updatedItems
      }
    
    case 'BULK_UPDATE_WORK_ORDERS_OPTIMISTIC':
      const updatedItemsOptimistic = state.workflowItems.map(item => {
        const update = action.payload.updates.find(u => u.id === item.id);
        if (update) {
          return { ...item, ...update.updates };
        }
        return item;
      });
      return {
        ...state,
        workflowItems: updatedItemsOptimistic
      }
    
    case 'REMOVE_WORK_ORDER':
      return {
        ...state,
        workflowItems: state.workflowItems.filter(item => item.id !== action.payload),
        totalCount: Math.max(0, state.totalCount - 1),
        hasNextPage: state.hasNextPage && state.workflowItems.length > state.pageSize
      }
    
    case 'OPEN_BULK_EDIT_MODAL':
      return { 
        ...state, 
        isBulkEditModalOpen: true,
        currentBulkEditField: action.payload
      }
    
    case 'CLOSE_BULK_EDIT_MODAL':
      return { 
        ...state, 
        isBulkEditModalOpen: false,
        currentBulkEditField: null
      }
    
    case 'SET_CURRENT_VIEW':
      return { 
        ...state, 
        currentView: action.payload,
        currentPage: 1, // Reset to first page when changing view
        selectedItems: [] // Clear selections when changing view
      }
    
    case 'SET_SAVED_VIEWS':
      return { ...state, savedViews: action.payload }
    
    case 'SET_UNSAVED_CHANGES':
      return { ...state, hasUnsavedChanges: action.payload }
    
    case 'ADD_CUSTOM_COLUMN':
      return { 
        ...state, 
        columns: [...state.columns, action.payload],
        visibleColumns: [...state.visibleColumns, action.payload]
      }
    
    case 'DELETE_COLUMN':
      return { 
        ...state, 
        columns: state.columns.filter(col => col.fieldConfigurationId !== action.payload),
        visibleColumns: state.visibleColumns.filter(col => col.fieldConfigurationId !== action.payload)
      }
    
    case 'SET_CONFIGURATION': {
      // Find the saved view that matches the viewSettingsId from configuration
      // Search all view sources: defaultView, userViews, sharedViews, and publicViews
      let matchingView: SavedView | null = null;
      
      if (action.payload.viewSettingsId && state.viewsResponse) {
        // Check default view first
        if (state.viewsResponse.defaultView?.viewSettingsId === action.payload.viewSettingsId) {
          matchingView = state.viewsResponse.defaultView;
        }
        // Check user views
        if (!matchingView) {
          matchingView = state.viewsResponse.userViews.find(v => v.viewSettingsId === action.payload.viewSettingsId) || null;
        }
        // Check shared views
        if (!matchingView) {
          matchingView = state.viewsResponse.sharedViews.find(v => v.viewSettingsId === action.payload.viewSettingsId) || null;
        }
        // Check public views
        if (!matchingView) {
          matchingView = state.viewsResponse.publicViews.find(v => v.viewSettingsId === action.payload.viewSettingsId) || null;
        }
        // Fallback to savedViews if still not found
        if (!matchingView) {
          matchingView = state.savedViews.find(view => view.viewSettingsId === action.payload.viewSettingsId) || null;
        }
      }

      // Build pinned columns map from configuration's stage configurations
      const pinnedFromConfig = (() => {
        try {
          const map: Record<string, string | null> = {}
          for (const sc of (action.payload.stageConfigurations || [])) {
            if (sc && sc.stageId) {
              map[sc.stageId] = (sc as any).pinnedColumnId ?? null
            }
          }
          return map
        } catch {
          return {}
        }
      })()

      let newConfiguration = action.payload
      
      // If custom fields were previously applied, re-apply them to the new configuration
      if (state.customFieldsApplied && state.activeClientFboId && state.cachedClientCustomFields.has(state.activeClientFboId)) {
        const cachedFields = state.cachedClientCustomFields.get(state.activeClientFboId)
        if (cachedFields) {
          // Helper function to apply custom fields to field configurations
          const applyCustomFieldsToFields = (fields: FieldConfiguration[]): FieldConfiguration[] => {
            return fields.map(field => {
              if (field.mappingSource === 'FboCustomField' && field.mappingSourceTargetColumn) {
                const customField = cachedFields.find(cf => cf.FieldName === field.mappingSourceTargetColumn)
                if (customField) {
                  return {
                    ...field,
                    columnNameEnglish: customField.DisplayName,
                    columnNameFrench: customField.DisplayName,
                    // Explicitly set customValues - either parsed array or empty array to clear stale values
                    customValues: customField.SelectValues 
                      ? customField.SelectValues.split(',').map(val => ({
                          code: val.trim(),
                          titleEnglish: val.trim(),
                          titleFrench: val.trim(),
                          color: '',
                          isDeleted: false
                        }))
                      : []
                  }
                }
              }
              return field
            })
          }
          
          newConfiguration = {
            ...newConfiguration,
            fieldsInUse: applyCustomFieldsToFields(newConfiguration.fieldsInUse),
            stageConfigurations: newConfiguration.stageConfigurations.map(stage => ({
              ...stage,
              fieldConfigurations: applyCustomFieldsToFields(stage.fieldConfigurations)
            }))
          }
        }
      }
      
      return { 
        ...state, 
        configuration: newConfiguration,
        stages: newConfiguration.stageConfigurations || [],
        pinnedColumnsByStage: pinnedFromConfig,
        isLoadingConfiguration: false,
        configurationError: null,
        // Store original configuration only if not already stored or if custom fields are not applied
        originalConfiguration: state.originalConfiguration || action.payload,
        // Apply saved filters and view settings if present
        ...(action.payload.savedGlobalFilters && {
          globalFilters: action.payload.savedGlobalFilters.map(filter => ({
            fieldConfigurationId: filter.fieldConfigurationId,
            filter: filter.filter,
            values: filter.values // Keep as string, don't parse to object
          }))
        }),
        ...(action.payload.savedStageFilters && {
          stageFilters: action.payload.savedStageFilters.map(filter => ({
            stageId: filter.stageId,
            fieldConfigurationId: filter.fieldConfigurationId,
            filter: filter.filter,
            values: filter.values ? JSON.parse(filter.values) : undefined
          }))
        }),
        // Apply saved column widths if present (deserialize from JSON string)
        ...(action.payload.columnWidths && {
          columnWidths: (() => {
            try {
              return JSON.parse(action.payload.columnWidths)
            } catch (error) {
              console.warn('Failed to deserialize columnWidths:', error)
              return {}
            }
          })()
        }),
        // Set current view based on viewSettingsId if present and different from current
        ...(action.payload.viewSettingsId && (!state.currentView || state.currentView.viewSettingsId !== action.payload.viewSettingsId) && {
          currentView: matchingView || null
        })
      }
    }
    
    case 'SET_LOADING_CONFIGURATION':
      return { ...state, isLoadingConfiguration: action.payload }
    
    case 'SET_CONFIGURATION_ERROR':
      return { ...state, configurationError: action.payload, isLoadingConfiguration: false }
    
    case 'SET_ASSIGNED_USERS':
      return { 
        ...state, 
        assignedUsers: action.payload,
        isLoadingAssignedUsers: false,
        assignedUsersError: null
      }
    
    case 'SET_LOADING_ASSIGNED_USERS':
      return { ...state, isLoadingAssignedUsers: action.payload }
    
    case 'SET_ASSIGNED_USERS_ERROR':
      return { 
        ...state, 
        assignedUsersError: action.payload,
        isLoadingAssignedUsers: false
      }
    
    case 'SET_STAGE_FILTERS':
      return { ...state, stageFilters: action.payload, hasUnsavedChanges: true }
    
    case 'SET_GLOBAL_FILTERS':
      return { ...state, globalFilters: action.payload, hasUnsavedChanges: true }
    
    case 'CLEAR_ALL_FILTERS':
      return { ...state, stageFilters: [], globalFilters: [], filters: {}, currentPage: 1 }
    
    case 'SET_FACETS':
      return { ...state, facets: action.payload }
    
    case 'SET_CODE_VALUES_DATA':
      return { 
        ...state, 
        codeValuesData: action.payload,
        isLoadingCodeValues: false,
        codeValuesError: null
      }
    
    case 'SET_LOADING_CODE_VALUES':
      return { ...state, isLoadingCodeValues: action.payload }
    
    case 'SET_CODE_VALUES_ERROR':
      return { 
        ...state, 
        codeValuesError: action.payload,
        isLoadingCodeValues: false
      }
    
    case 'SET_COLUMN_WIDTHS':
      return { 
        ...state, 
        columnWidths: action.payload,
        hasUnsavedChanges: true
      }

    case 'SET_PINNED_COLUMN':
      return {
        ...state,
        pinnedColumnsByStage: {
          ...(state.pinnedColumnsByStage || {}),
          [action.payload.stageId]: action.payload.columnId,
        },
        hasUnsavedChanges: true,
      }
    case 'SET_PINNED_COLUMNS_BY_STAGE':
      return {
        ...state,
        pinnedColumnsByStage: action.payload,
      }
    
    case 'SET_VIEWS_RESPONSE':
      return { ...state, viewsResponse: action.payload }
    
    case 'CACHE_CLIENT_CUSTOM_FIELDS': {
      const newCache = new Map(state.cachedClientCustomFields)
      newCache.set(action.payload.clientFboId, action.payload.customFields)
      return {
        ...state,
        cachedClientCustomFields: newCache
      }
    }

    case 'APPLY_CUSTOM_FIELDS': {
      if (!state.configuration) return state

      const { customFields, clientFboId, clientName } = action.payload
      
      // Store original configuration if not already stored
      const originalConfig = state.originalConfiguration || state.configuration

      // Helper function to apply custom fields to field configurations
      const applyCustomFieldsToFields = (fields: FieldConfiguration[]): FieldConfiguration[] => {
        return fields.map(field => {
          if (field.mappingSource === 'FboCustomField' && field.mappingSourceTargetColumn) {
            const customField = customFields.find(cf => cf.FieldName === field.mappingSourceTargetColumn)
            if (customField) {
              return {
                ...field,
                columnNameEnglish: customField.DisplayName,
                columnNameFrench: customField.DisplayName,
                // Explicitly set customValues - either parsed array or empty array to clear stale values
                customValues: customField.SelectValues 
                  ? customField.SelectValues.split(',').map(val => ({
                      code: val.trim(),
                      titleEnglish: val.trim(),
                      titleFrench: val.trim(),
                      color: '',
                      isDeleted: false
                    }))
                  : []
              }
            }
          }
          return field
        })
      }

      // Update configuration - IMPORTANT: Apply to originalConfig, not current state.configuration
      // This ensures when switching from Client A to Client B, we start fresh from the original
      const updatedConfig = {
        ...originalConfig,
        fieldsInUse: applyCustomFieldsToFields(originalConfig.fieldsInUse),
        stageConfigurations: originalConfig.stageConfigurations.map(stage => ({
          ...stage,
          fieldConfigurations: applyCustomFieldsToFields(stage.fieldConfigurations)
        }))
      }

      return {
        ...state,
        configuration: updatedConfig,
        originalConfiguration: originalConfig,
        activeClientFboId: clientFboId,
        activeClientName: clientName,
        customFieldsApplied: true
      }
    }

    case 'CLEAR_CUSTOM_FIELDS': {
      if (!state.configuration || !state.originalConfiguration) return state
      
      // Helper function to revert custom field changes without removing fields from stages
      const revertCustomFieldsInFields = (fields: FieldConfiguration[]): FieldConfiguration[] => {
        return fields.map(field => {
          if (field.mappingSource === 'FboCustomField' && field.mappingSourceTargetColumn) {
            // Find the original field configuration
            const originalField = state.originalConfiguration?.fieldsInUse.find(
              f => f.fieldConfigurationId === field.fieldConfigurationId
            )
            if (originalField) {
              return {
                ...field,
                columnNameEnglish: originalField.columnNameEnglish,
                columnNameFrench: originalField.columnNameFrench,
                customValues: originalField.customValues
              }
            }
          }
          return field
        })
      }

      // Revert only the custom field properties, keep the stage structure
      const revertedConfig = {
        ...state.configuration,
        fieldsInUse: revertCustomFieldsInFields(state.configuration.fieldsInUse),
        stageConfigurations: state.configuration.stageConfigurations.map(stage => ({
          ...stage,
          fieldConfigurations: revertCustomFieldsInFields(stage.fieldConfigurations)
        }))
      }

      return {
        ...state,
        configuration: revertedConfig,
        activeClientFboId: null,
        activeClientName: null,
        customFieldsApplied: false
      }
    }
    
    case 'SET_HANDLING_CLIENT_FILTER_CHANGE':
      return { ...state, isHandlingClientFilterChange: action.payload }
    
    default:
      return state
  }
}

interface WorkflowContextType {
  // State
  state: WorkflowState
  currentUser: User
  currentStage?: WorkflowStage
  isFilterDrawerOpen: boolean
  isBulkEditModalOpen: boolean
  isLoadingColumns: boolean
  savedViews: SavedView[]
  currentView: SavedView | null
  hasUnsavedChanges: boolean
  filters: Filters
  
  // View data from API
  viewsResponse: UserViewsResponse | null
  
  // Data properties for WorkflowGrid
  workOrders: WorkflowItem[] // Alias for workflowItems
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  columns: FieldConfiguration[]
  visibleColumns: FieldConfiguration[]
  selectedItems: string[]
  currentPage: number
  pageSize: number
  
  // Loading states
  isLoadingWorkOrders: boolean
  isLoadingRows: boolean
  
  // Actions
  setCurrentStage: (stage: WorkflowStage) => void
  toggleFilterDrawer: () => void
  updateFilter: (key: string, value: any) => void
  clearFilters: () => void
  setSelectedItems: (items: string[]) => void
  toggleItemSelection: (itemId: string) => void
  selectAllItems: () => void
  deselectAllItems: () => void
  setCurrentPage: (page: number) => void
  setPageSize: (size: number) => void
  setItemsPerPage: (itemsPerPage: number | null) => void
  updateVisibleColumns: (columns: FieldConfiguration[]) => void
  
  // Data operations
  refreshData: (customFilterArray?: string[], orderBy?: string, facetKeys?: string[], targetStage?: WorkflowStage) => Promise<void>
  updateWorkOrderField: (orderId: string, field: string, value: any) => Promise<boolean>
  updateWorkOrderFieldOptimistically: (orderId: string, field: string, value: any) => void
  removeWorkOrder: (orderId: string) => void
  removeWorkOrdersBulk: (orderIds: string[]) => void
  bulkUpdateWorkOrderField: (field: string, value: any) => Promise<void>
  updateWorkOrdersOptimistically: (updates: { id: string; updates: Record<string, any> }[]) => void
  processAction: (orderId: string, action: string) => Promise<void>
  bulkAssignWorkOrders: (userNames: string[]) => Promise<void>
  
  // Modal operations
  openBulkEditModal: (field: string) => void
  closeBulkEditModal: () => void
  
  // View operations
  setCurrentView: (view: SavedView) => Promise<void>
  setSavedViews: (views: SavedView[]) => void
  setUnsavedChanges: (hasChanges: boolean) => void
  saveCurrentView: () => Promise<void>
  saveViewAs: (name: string, isPublic?: boolean) => Promise<SavedView>
  deleteView: (viewId: string) => Promise<void>
  updateViewMetadata: (title: string, isPublic: boolean, isTitleAndVisibilityUpdate?: boolean, saveFilters?: boolean) => Promise<void>
  refreshViews: () => Promise<UserViewsResponse | null>
  
  // Column operations
  addCustomColumn: (column: FieldConfiguration) => void
  deleteColumn: (columnId: string) => void

  // Configuration operations
  refreshConfiguration: (force?: boolean, viewId?: string | null, skipFilterUpdate?: boolean) => Promise<void>
  updateConfigurationDirectly: (config: WorkflowConfiguration) => void
  configuration: WorkflowConfiguration | undefined
  isLoadingConfiguration: boolean
  configurationError: string | null

  // Stages
  stages: StageConfiguration[]

  // Assigned Users operations
  refreshAssignedUsers: () => Promise<void>
  assignedUsers: AssignedUser[]
  isLoadingAssignedUsers: boolean
  assignedUsersError: string | null

  // Facets
  facets?: Record<string, { value: string; count: number }[]>

  // New operations
  setStageFilters: (filters: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[]) => void
  setGlobalFilters: (filters: { fieldConfigurationId: string; filter: string; values?: string }[]) => void
  clearAllFilters: () => void

  // New state for stageFilters and globalFilters
  stageFilters: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[]
  globalFilters: { fieldConfigurationId: string; filter: string; values?: string }[]

  // New state for work orders facets
  workOrdersFacets: Record<string, { value: string; count: number }[]> | undefined

  // Code Values
  codeValues: ReturnType<typeof useCodeValues>
  setCodeValues: (codeValues: any) => void
  
  // Code Values State
  codeValuesData?: Record<string, any[]>
  isLoadingCodeValues: boolean
  codeValuesError: string | null
  refreshCodeValues: () => Promise<void>

  // Column Widths operations
  setColumnWidths: (columnWidths: Record<string, number>) => void

  // Pinned column operations
  setPinnedColumn: (columnId: string | null) => void

  // Save view state silently (for visibility updates, etc.)
  saveViewStateSilently: (updatedPinnedColumns?: Record<string, string | null>, viewsResponseOverride?: UserViewsResponse | null) => Promise<void>
}

interface WorkflowProviderProps {
  children: ReactNode
}

export function WorkflowProvider({ children }: WorkflowProviderProps) {
  const [state, dispatch] = useReducer(workflowReducer, initialState)
  const workflowApi = useWorkflowApi()
  const { token, isAuthenticated } = useAuth()
  const { toast } = useToast()
  const codeValues = useCodeValues()

  // Pinned columns come from configuration; no local persistence required

  // Fetch views on component mount
  useEffect(() => {
    const loadViews = async () => {
      if (!isAuthenticated || !token) {
        return
      }
      
      try {
        const viewsResponse = await workflowApi.fetchViews()
        
        // Store the original API response structure
        const allViews: SavedView[] = []
        
        if (viewsResponse.defaultView) {
          allViews.push(viewsResponse.defaultView)
        }
        
        allViews.push(...viewsResponse.userViews)
        allViews.push(...viewsResponse.sharedViews)
        
        // Set the default view as current view
        const currentView = viewsResponse.defaultView || null
        
        dispatch({ type: 'SET_SAVED_VIEWS', payload: allViews })
        dispatch({ type: 'SET_CURRENT_VIEW', payload: currentView })
        dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })
      } catch (error: any) {
        console.error('Failed to fetch views:', error)
        // Keep empty state if fetch fails
      }
    }

    loadViews()
  }, [isAuthenticated, token]) // Add authentication dependencies

  // Load columns dynamically from stageConfiguration
  const loadColumns = useCallback(() => {
    if (!state.configuration || !state.currentStage) return;
    const currentStageConfig = state.stages.find(
      s => s.stageCode.toLowerCase() === state.currentStage!.toLowerCase()
    );
    if (!currentStageConfig) return;

    // Use the actual fieldConfigurations as columns
    const columns = currentStageConfig.fieldConfigurations;
    const visibleColumns = columns;

    dispatch({ type: 'SET_COLUMNS', payload: { columns, visibleColumns } });
  }, [state.configuration, state.currentStage, state.stages]);

  // Refresh work order data
  const refreshData = useCallback(async (customFilterArray?: string[], orderBy?: string, facetKeys?: string[], targetStage?: WorkflowStage) => {
    if (!isAuthenticated || !token) {
      return;
    }
    try {
      // Only set loading state if not already loading (to preserve stage change loading state)
      if (state.loadingState === LoadingState.IDLE) {
        dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.LOADING_WORK_ORDERS })
      }
      
      // Use targetStage if provided, otherwise use state.currentStage
      let currentStage = targetStage || state.currentStage
      if (!currentStage && state.stages.length > 0) {
        currentStage = state.stages[0].stageCode as WorkflowStage
        dispatch({ type: 'SET_CURRENT_STAGE', payload: currentStage })
      }
      
      if (!currentStage) {
        console.error("No current stage available")
        return;
      }
      
      // Find the current stage configuration
      const currentStageConfig = state.stages.find(s => s.stageCode === currentStage)
      if (!currentStageConfig) {
        throw new Error(`No stage configuration found for current stage '${currentStage}'. Available stages: ${state.stages.map(s => s.stageCode).join(', ')}`)
      }
      
      // Build select string from fieldConfigurations, excluding static fields
      let selectFields = currentStageConfig && currentStageConfig.fieldConfigurations.length > 0
        ? currentStageConfig.fieldConfigurations
            .filter(f => !f.isStaticField) // Filter out static fields
            .map(f => f.columnCode)
        : [];
      
      // Always include fields from configuration, or use default list if not provided
      const alwaysInclude = state.configuration?.alwaysFetchedFieldCodes || ["FboId", "ObId", "RecordType", "InformationRequestType"];
      for (const field of alwaysInclude) {
        if (!selectFields.includes(field)) {
          selectFields.push(field);
        }
      }

      const select = selectFields.length > 0 ? selectFields.join(",") : "*";
      
      // Determine page size based on itemsPerPage setting
      const effectivePageSize = state.itemsPerPage !== null ? state.itemsPerPage : state.pageSize;
      
      // API call: Search workflow items with current stage and pagination
      const response = await workflowApi.searchWorkflowItems(
        currentStage,
        customFilterArray,
        currentStageConfig.fieldConfigurations,
        state.currentPage,
        effectivePageSize,
        '*', // search term
        select,
        undefined, // filter
        orderBy,
        facetKeys
      )
      
      // Response received: Update state with workflow data
      dispatch({ type: 'SET_WORK_ORDERS', payload: response })
    } catch (error) {
      console.error('Failed to refresh data:', error)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load workflow data' })
    }
  }, [state.currentStage, state.currentPage, state.pageSize, state.itemsPerPage, state.stages, isAuthenticated, token])

  // Track if initial data has been loaded
  const hasInitialDataLoaded = useRef(false)

  // Load initial data when configuration is ready
  useEffect(() => {
    if (!state.configuration) return;
    if (hasInitialDataLoaded.current) return;
    
    // Configuration ready: Load columns and initial data
    loadColumns();
    
    // Then load data after a short delay to ensure columns are set
    setTimeout(() => {
      refreshData();
      hasInitialDataLoaded.current = true;
    }, 100);
    
  }, [state.configuration, loadColumns, refreshData]);



  // Update a single work order field
  const updateWorkOrderField = async (orderId: string, field: string, value: any): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.UPDATING_WORK_ORDER })
      
      // Call the API first - NO optimistic update
      const result = await workflowApi.updateWorkflowItemField(orderId, field, value)
      
      // Handle the response structure
      const { successUpdateCount, failedUpdateCount, messages } = result;
      
      if (successUpdateCount > 0) {
        // Success: Update the UI with the new value
        dispatch({ 
          type: 'UPDATE_WORK_ORDER_FIELD', 
          payload: { orderId, field, value } 
        })
        
        dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
        return true; // Success
      }
      
      if (failedUpdateCount > 0) {
        // Failed: Show error message (UI was never changed, so no need to revert)
        const errorDetails = messages && messages.length > 0 
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}` 
          : '';
        
        toast({
          variant: "destructive",
          title: "Update Failed",
          description: `Failed to update field${errorDetails}`,
        });
      }
      
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
      return false; // Failed

    } catch (error) {
      console.error('Failed to update work order field:', error)
      
      // Exception: Show error message (UI was never changed, so no need to revert or refresh)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update field' })
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update field",
      })
      
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
      return false; // Failed
    }
  }

  // Optimistic single field update - updates UI immediately without waiting for API
  const updateWorkOrderFieldOptimistically = (orderId: string, field: string, value: any) => {
    try {
      // Update local state immediately
      dispatch({ 
        type: 'UPDATE_WORK_ORDER_FIELD', 
        payload: { orderId, field, value } 
      })
      
      // Call the API in the background without waiting
      workflowApi.updateWorkflowItemField(orderId, field, value)
        .then(() => {
        })
        .catch((error) => {
          // Revert the optimistic update on error
          // Don't refresh data - just show error message and let user continue
          toast({
            variant: "destructive",
            title: "Error",
            description: error instanceof Error ? error.message : "Failed to update field",
          })
        })
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update field",
      })
    }
  }

  // Bulk update work order field
  const bulkUpdateWorkOrderField = async (field: string, value: any) => {
    if (state.selectedItems.length === 0) return

    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.BULK_UPDATING })
      
      await workflowApi.bulkUpdateWorkflowItems(state.selectedItems, field, value)
      
      // Update local state for all selected items
      state.selectedItems.forEach(orderId => {
        dispatch({ 
          type: 'UPDATE_WORK_ORDER_FIELD', 
          payload: { orderId, field, value } 
        })
      })
      
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
      
    } catch (error) {
      console.error('Failed to bulk update work order field:', error)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to bulk update field' })
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to bulk update field",
      })
    }
  }

  // Optimistic bulk update function - updates UI immediately without server refresh
  const updateWorkOrdersOptimistically = (updates: { id: string; updates: Record<string, any> }[]) => {
    try {      
      // Check if we're already processing an update to prevent duplicates
      if (state.loadingState === LoadingState.BULK_UPDATING) {
        return;
      }
      
      // Update local state immediately
      dispatch({ 
        type: 'BULK_UPDATE_WORK_ORDERS_OPTIMISTIC', 
        payload: { updates } 
      })
      
      toast({
        variant: "success",
        title: "Bulk update successful",
        description: `Updated ${updates.length} items successfully.`,
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update items",
      })
    }
  }

  // Process workflow action
  const processAction = async (orderId: string, action: string) => {
    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.UPDATING_WORK_ORDER })
      
      const updatedItem = await workflowApi.processWorkflowAction(orderId, action)
      
      // Update local state with the updated item
      dispatch({ 
        type: 'UPDATE_WORK_ORDER_FIELD', 
        payload: { orderId, field: 'stage', value: updatedItem.stage } 
      })
      
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
      toast({
        variant: "success",
        title: "Success",
        description: "Action processed successfully",
      })
    } catch (error) {
      console.error('Failed to process action:', error)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to process action' })
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process action",
      })
    }
  }

  // Bulk assign work orders
  const bulkAssignWorkOrders = async (userNames: string[]) => {
    if (state.selectedItems.length === 0) return

    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.BULK_UPDATING })
      
      // For now, simulate bulk assignment by updating each order individually
      const results: WorkflowItem[] = []
      
      for (const orderId of state.selectedItems) {
        const updatedOrder = await workflowApi.updateWorkflowItemField(
          orderId, 
          'assignedToUserFullName', 
          userNames[0] // Use the first user name for simplicity
        )
        results.push(updatedOrder)
      }
      
      // Update local state for all selected items
      state.selectedItems.forEach(orderId => {
        dispatch({ 
          type: 'UPDATE_WORK_ORDER_FIELD', 
          payload: { orderId, field: 'assignedToUserFullName', value: userNames[0] } 
        })
      })
      
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
      toast({
        variant: "success",
        title: "Success",
        description: `Assigned ${state.selectedItems.length} items to ${userNames[0]}`,
      })
    } catch (error) {
      console.error('Failed to bulk assign work orders:', error)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to bulk assign work orders' })
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to bulk assign work orders",
      })
    }
  }

  // Modal operations
  const openBulkEditModal = useCallback((field: string) => {
    dispatch({ type: 'OPEN_BULK_EDIT_MODAL', payload: field })
  }, [dispatch])

  const closeBulkEditModal = useCallback(() => {
    dispatch({ type: 'CLOSE_BULK_EDIT_MODAL' })
  }, [dispatch])

  const setSavedViews = useCallback((views: SavedView[]) => {
    dispatch({ type: 'SET_SAVED_VIEWS', payload: views })
  }, [dispatch])

  const setUnsavedChanges = useCallback((hasChanges: boolean) => {
    dispatch({ type: 'SET_UNSAVED_CHANGES', payload: hasChanges })
  }, [dispatch])

  // Column operations
  const addCustomColumn = useCallback((column: FieldConfiguration) => {
    dispatch({ type: 'ADD_CUSTOM_COLUMN', payload: column })
  }, [dispatch])

  const deleteColumn = useCallback((columnId: string) => {
    dispatch({ type: 'DELETE_COLUMN', payload: columnId })
  }, [dispatch])

  // Save current view
  const saveCurrentView = useCallback(async () => {
    if (!state.currentView?.viewSettingsId) {
      return
    }

    try {
      // Build stage configurations from current state
      const stageConfigurations = state.stages.map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: (state.pinnedColumnsByStage || {})[stage.stageId] || null,
      }))

      // Serialize stage filters with values as JSON strings
      const serializedStageFilters = state.stageFilters.map(filter => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined
      }))

      // For default views, preserve title, isPublic, and isDefault from original view
      const isDefaultView = state.currentView.isDefault === true
      const request = {
        id: state.currentView.viewSettingsId,
        title: isDefaultView ? state.currentView.title : state.currentView.title,
        isDefault: isDefaultView ? state.currentView.isDefault : false, 
        isPublic: isDefaultView ? state.currentView.isPublic : (state.currentView.isPublic ?? false),
        sharedUserIds: [], // TODO: Get from UI when sharing is implemented
        stageConfigurations,
        stageFilters: serializedStageFilters,
        globalFilters: state.globalFilters,
        columnWidths: JSON.stringify(state.columnWidths || {}), // Always include column widths
        itemsPerPage: state.itemsPerPage,
        isUserViewStateUpdate: true,
        updateFilters: true
      }

      await workflowApi.saveViewSettings(request)
      
      // Refresh views after saving
      const viewsResponse = await workflowApi.fetchViews()
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })
      
      // Clear unsaved changes
      dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false })
    } catch (error) {
      console.error('Failed to save current view:', error)
      throw error
    }
  }, [state.currentView, state.stages, state.stageFilters, state.globalFilters, workflowApi, dispatch])

  // Save view as
  const saveViewAs = useCallback(async (name: string, isPublic: boolean = false) => {
    try {
      // Build stage configurations from current state
      const stageConfigurations = state.stages.map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: (state.pinnedColumnsByStage || {})[stage.stageId] || null,
      }))

      // Serialize stage filters with values as JSON strings
      const serializedStageFilters = state.stageFilters.map(filter => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined
      }))

      const request = {
        // Don't set id for "Save as new" - it will be created
        title: name,
        isDefault: false, // TODO: Determine if this should be default
        isPublic: isPublic,
        sharedUserIds: [], // TODO: Get from UI when sharing is implemented
        stageConfigurations,
        stageFilters: serializedStageFilters,
        globalFilters: state.globalFilters,
        columnWidths: JSON.stringify(state.columnWidths || {}), // Always include column widths
        itemsPerPage: state.itemsPerPage,
        isUserViewStateUpdate: true
      }

      const response = await workflowApi.saveViewSettings(request)
      
      // Refresh views after saving
      const viewsResponse = await workflowApi.fetchViews()
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })
      
      // Clear unsaved changes
      dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false })
      
      // Find the newly created view and return it
      const allViews: SavedView[] = []
      if (viewsResponse.defaultView) {
        allViews.push(viewsResponse.defaultView)
      }
      allViews.push(...viewsResponse.userViews)
      allViews.push(...viewsResponse.sharedViews)
      
      // Find the newly created view by matching title and isPublic, but exclude the current view
      const newlyCreatedView = allViews.find(view => 
        view.title === name && 
        view.isPublic === isPublic &&
        view.viewSettingsId !== state.currentView?.viewSettingsId
      )
      
      // Also update the savedViews state to ensure consistency
      dispatch({ type: 'SET_SAVED_VIEWS', payload: allViews })
      
      return newlyCreatedView || response
    } catch (error) {
      console.error('Failed to save view as:', error)
      throw error
    }
  }, [state.stages, state.stageFilters, state.globalFilters, workflowApi, dispatch])

  // Update view metadata (title and isPublic)
  const updateViewMetadata = useCallback(async (title: string, isPublic: boolean, isTitleAndVisibilityUpdate: boolean = false, saveFilters: boolean = false) => {
    if (!state.currentView?.viewSettingsId) {
      return
    }

    try {
      // Build stage configurations from current state
      const stageConfigurations = state.stages.map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: (state.pinnedColumnsByStage || {})[stage.stageId] || null,
      }))

      // Serialize stage filters with values as JSON strings
      const serializedStageFilters = state.stageFilters.map(filter => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined
      }))

      const request = {
        id: state.currentView.viewSettingsId,
        title: title, // Use the new title
        isDefault: false, 
        isPublic: isPublic, // Use the new isPublic value
        sharedUserIds: [],
        stageConfigurations,
        stageFilters: serializedStageFilters,
        globalFilters: state.globalFilters,
        columnWidths: JSON.stringify(state.columnWidths || {}), // Always include column widths
        itemsPerPage: state.itemsPerPage,
        isTitleAndVisibilityUpdate: isTitleAndVisibilityUpdate,
        isUserViewStateUpdate: true,
        updateFilters: saveFilters
      }

      await workflowApi.saveViewSettings(request)
      
      // Refresh views after saving
      const viewsResponse = await workflowApi.fetchViews()
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })
      
      // Update current view with new metadata
      const updatedView = viewsResponse.userViews.find(v => v.viewSettingsId === state.currentView?.viewSettingsId) ||
                         viewsResponse.sharedViews.find(v => v.viewSettingsId === state.currentView?.viewSettingsId)
      
      if (updatedView) {
        dispatch({ type: 'SET_CURRENT_VIEW', payload: updatedView })
      }
      
      // Clear unsaved changes
      dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false })
    } catch (error) {
      console.error('Failed to update view metadata:', error)
      throw error
    }
  }, [state.currentView, state.stages, state.stageFilters, state.globalFilters, workflowApi, dispatch])

  // Update configuration immediately without fetching from server
  const updateConfigurationDirectly = useCallback((config: WorkflowConfiguration) => {
    const configToDispatch = { ...config, savedGlobalFilters: undefined, savedStageFilters: undefined }
    dispatch({ type: 'SET_CONFIGURATION', payload: configToDispatch })
  }, [dispatch])

  // Configuration operations
  const refreshConfiguration = useCallback(async (force?: boolean, viewId?: string | null, skipFilterUpdate?: boolean) => {
    if (!isAuthenticated || !token) {
      return;
    }
    
    try {
      dispatch({ type: 'SET_LOADING_CONFIGURATION', payload: true })
      
      const configuration = await workflowApi.fetchWorkflowConfiguration(viewId)
      // If skipFilterUpdate is true, remove saved filters from configuration before dispatching
      // This prevents overwriting current filter state
      const configToDispatch = skipFilterUpdate
        ? { ...configuration, savedGlobalFilters: undefined, savedStageFilters: undefined }
        : configuration
      dispatch({ type: 'SET_CONFIGURATION', payload: configToDispatch })
      
      // Set itemsPerPage from configuration if available
      if (configuration.itemsPerPage !== undefined) {
        dispatch({ type: 'SET_ITEMS_PER_PAGE', payload: configuration.itemsPerPage })
      }
      
      // Set current view based on viewSettingsId in configuration
      if (configuration.viewSettingsId && state.viewsResponse) {
        // Find the view that matches the viewSettingsId
        let targetView: SavedView | null = null;
        
        // Check default view
        if (state.viewsResponse.defaultView?.viewSettingsId === configuration.viewSettingsId) {
          targetView = state.viewsResponse.defaultView;
        }
        
        // Check user views
        if (!targetView) {
          targetView = state.viewsResponse.userViews.find(v => v.viewSettingsId === configuration.viewSettingsId) || null;
        }
        
        // Check shared views
        if (!targetView) {
          targetView = state.viewsResponse.sharedViews.find(v => v.viewSettingsId === configuration.viewSettingsId) || null;
        }
        
        // Check public views
        if (!targetView) {
          targetView = state.viewsResponse.publicViews.find(v => v.viewSettingsId === configuration.viewSettingsId) || null;
        }
        
        // Set the current view if found
        if (targetView) {
          dispatch({ type: 'SET_CURRENT_VIEW', payload: targetView });
        }
      }
    } catch (error) {
      console.error('Failed to refresh configuration:', error)
      dispatch({ type: 'SET_CONFIGURATION_ERROR', payload: 'Failed to load configuration' })
    }
  }, [isAuthenticated, token])

  // Assigned Users operations
  const refreshAssignedUsers = useCallback(async () => {
    if (!isAuthenticated || !token) {
      return;
    }
    
    try {
      dispatch({ type: 'SET_LOADING_ASSIGNED_USERS', payload: true })
      
      const assignedUsers = await workflowApi.fetchAssignedUsers()
      dispatch({ type: 'SET_ASSIGNED_USERS', payload: assignedUsers })
    } catch (error) {
      console.error('Failed to refresh assigned users:', error)
      dispatch({ type: 'SET_ASSIGNED_USERS_ERROR', payload: 'Failed to load assigned users' })
    }
  }, [isAuthenticated, token])

  // View operations
  const setCurrentView = useCallback(async (view: SavedView) => {
    dispatch({ type: 'SET_CURRENT_VIEW', payload: view })
    
    // Clear unsaved changes when switching views
    dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false })
    
    // First fetch configuration with the new view ID and wait for it to complete
    const configuration = await workflowApi.fetchWorkflowConfiguration(view.viewSettingsId)
    dispatch({ type: 'SET_CONFIGURATION', payload: configuration })
    
    // Then call POST view-state endpoint with the freshly loaded configuration
    try {
      // Build stage configurations from the freshly fetched configuration (not old state.stages)
      const stageConfigurations = (configuration?.stageConfigurations || []).map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: stage.pinnedColumnId ?? null,
      }))

      // Get all visible view IDs from the views response
      const visibleViewIds: string[] = [];
      
      // Add default view if it exists and is visible
      if (state.viewsResponse?.defaultView && state.viewsResponse.defaultView.isVisible !== false) {
        visibleViewIds.push(state.viewsResponse.defaultView.viewSettingsId);
      }
      
      // Add user views that are visible
      if (state.viewsResponse?.userViews) {
        state.viewsResponse.userViews.forEach(view => {
          if (view.isVisible !== false) {
            visibleViewIds.push(view.viewSettingsId);
          }
        });
      }
      
      // Add shared views that are visible
      if (state.viewsResponse?.sharedViews) {
        state.viewsResponse.sharedViews.forEach(view => {
          if (view.isVisible !== false) {
            visibleViewIds.push(view.viewSettingsId);
          }
        });
      }
      
      // Add public views that are visible
      if (state.viewsResponse?.publicViews) {
        state.viewsResponse.publicViews.forEach(view => {
          if (view.isVisible !== false) {
            visibleViewIds.push(view.viewSettingsId);
          }
        });
      }

      const viewStateRequest = {
        id: view.viewSettingsId,
        title: view.title,
        isDefault: view.isDefault ?? false,
        isPublic: view.isPublic ?? false,
        sharedUserIds: [],
        stageConfigurations,
        stageFilters: configuration?.savedStageFilters || [], // Use saved stage filters from freshly loaded config
        globalFilters: (configuration?.savedGlobalFilters || []).map(filter => ({
          fieldConfigurationId: filter.fieldConfigurationId,
          filter: filter.filter,
          values: typeof filter.values === 'string' ? filter.values : (filter.values ? JSON.stringify(filter.values) : undefined)
        })),
        visibleViewIds: visibleViewIds,
        itemsPerPage: state.itemsPerPage,
        columnWidths: JSON.stringify(state.columnWidths || {}), // Always include column widths
        isUserViewStateUpdate: true
      }
      
      await workflowApi.saveViewState(viewStateRequest)
    } catch (error) {
      console.error('Failed to save view state when switching views:', error)
    }
  }, [dispatch, state.stages, state.viewsResponse, workflowApi])

  const refreshViews = useCallback(async (): Promise<UserViewsResponse | null> => {
    try {
      const viewsResponse = await workflowApi.fetchViews()
      
      // Store the original API response structure
      const allViews: SavedView[] = []
      
      if (viewsResponse.defaultView) {
        allViews.push(viewsResponse.defaultView)
      }
      
      allViews.push(...viewsResponse.userViews)
      allViews.push(...viewsResponse.sharedViews)
      
      dispatch({ type: 'SET_SAVED_VIEWS', payload: allViews })
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })
      return viewsResponse
    } catch (error: any) {
      console.error('Failed to refresh views:', error)
      // Keep existing state if refresh fails
      return null
    }
  }, [workflowApi, dispatch])

  // Load configuration and assigned users on mount
  useEffect(() => {
    if (isAuthenticated && token) {
      refreshConfiguration(undefined, null)
      refreshAssignedUsers()
    }
  }, [isAuthenticated, token, refreshConfiguration, refreshAssignedUsers])

  // Add this useEffect after configuration and assignedUsers are loaded
  useEffect(() => {
    async function fetchGlobalFacetsOnce() {
      if (
        !state.configuration ||
        !Array.isArray(state.configuration.globalFilterIds) ||
        state.configuration.globalFilterIds.length === 0 ||
        !state.assignedUsers || state.assignedUsers.length === 0
      ) {
        return;
      }
      // Build facet keys and select fields
      const facetKeys = getFacetKeysFromConfiguration(state.configuration);
      const selectFields = getSelectFieldsFromConfiguration(state.configuration);
      // Fetch facets
      try {
        // Use column codes for SELECT (to get full objects), facetKeys for facets (to aggregate by sub-properties)
        const select = selectFields.join(',');
        let filter = `(Stage ne '' and Stage ne null and Stage ne 'Discarded' and IsDeleted eq false)`;
        const response = await workflowApi.searchWorkflowItems(
          undefined,
          undefined,
          [],
          1,
          0,
          '*',
          select,
          filter,
          undefined,
          facetKeys
        );
        dispatch({ type: 'SET_FACETS', payload: response.facets });
      } catch (err) {
        console.error('Failed to fetch global facets', err);
      }
    }
    fetchGlobalFacetsOnce();
  }, [state.configuration, state.assignedUsers]);

  // Set current view when configuration and savedViews are both loaded
  // Only set if no current view exists (initial load) or if current view doesn't match configuration
  useEffect(() => {
    if (state.configuration && state.configuration.viewSettingsId && state.viewsResponse) {
      // Check if current view matches the configuration's viewSettingsId
      const currentViewMatches = state.currentView?.viewSettingsId === state.configuration.viewSettingsId;
      
      // If current view doesn't match or doesn't exist, find and set the correct view
      if (!currentViewMatches) {
        let matchingView: SavedView | null = null;
        
        // Check default view first
        if (state.viewsResponse.defaultView?.viewSettingsId === state.configuration.viewSettingsId) {
          matchingView = state.viewsResponse.defaultView;
        }
        // Check user views
        if (!matchingView) {
          matchingView = state.viewsResponse.userViews.find(v => v.viewSettingsId === state.configuration!.viewSettingsId) || null;
        }
        // Check shared views
        if (!matchingView) {
          matchingView = state.viewsResponse.sharedViews.find(v => v.viewSettingsId === state.configuration!.viewSettingsId) || null;
        }
        // Check public views
        if (!matchingView) {
          matchingView = state.viewsResponse.publicViews.find(v => v.viewSettingsId === state.configuration!.viewSettingsId) || null;
        }
        // Fallback to savedViews
        if (!matchingView) {
          matchingView = state.savedViews.find(view => view.viewSettingsId === state.configuration!.viewSettingsId) || null;
        }
        
        if (matchingView) {
          dispatch({ type: 'SET_CURRENT_VIEW', payload: matchingView });
        }
      }
    }
  }, [state.configuration, state.savedViews, state.viewsResponse, state.currentView, dispatch]);

  // Computed values
  const currentUser = state.currentUser
  const currentStage = state.currentStage
  const isFilterDrawerOpen = state.isFilterDrawerOpen
  const isBulkEditModalOpen = state.isBulkEditModalOpen
  const isLoadingColumns = state.loadingState === LoadingState.LOADING_COLUMNS
  const savedViews = state.savedViews
  const currentView = state.currentView
  const hasUnsavedChanges = state.hasUnsavedChanges
  const filters = state.filters
  const workOrders = state.workflowItems
  const totalCount = state.totalCount
  const hasNextPage = state.hasNextPage
  const hasPreviousPage = state.hasPreviousPage
  const columns = state.columns
  const visibleColumns = state.visibleColumns
  const selectedItems = state.selectedItems
  const currentPage = state.currentPage
  const pageSize = state.pageSize
  const isLoadingWorkOrders = state.loadingState === LoadingState.LOADING_WORK_ORDERS
  const isLoadingRows = state.loadingState === LoadingState.LOADING_ROWS
  const configuration = state.configuration
  const isLoadingConfiguration = state.isLoadingConfiguration ?? false
  const configurationError = state.configurationError ?? null
  const stages = state.stages
  const assignedUsers = state.assignedUsers
  const isLoadingAssignedUsers = state.isLoadingAssignedUsers ?? false
  const assignedUsersError = state.assignedUsersError ?? null
  const facets = state.facets

  // Actions
  const setCurrentStage = useCallback((stage: WorkflowStage) => {
    dispatch({ type: 'SET_CURRENT_STAGE', payload: stage })
    // When stage changes, check if we need to update client-specific custom fields
    // This handles the case where different stages have different stage-level client filters
    if (state.configuration) {
      dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: true })
    }
  }, [dispatch, state.configuration])

  const toggleFilterDrawer = useCallback(() => {
    dispatch({ type: 'TOGGLE_FILTER_DRAWER' })
  }, [dispatch])

  const updateFilter = useCallback((key: string, value: any) => {
    dispatch({ type: 'UPDATE_FILTER', payload: { key, value } })
  }, [dispatch])

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_FILTERS' })
  }, [dispatch])

  const setSelectedItems = useCallback((items: string[]) => {
    dispatch({ type: 'SET_SELECTED_ITEMS', payload: items })
  }, [dispatch])

  const toggleItemSelection = useCallback((itemId: string) => {
    dispatch({ type: 'TOGGLE_ITEM_SELECTION', payload: itemId })
  }, [dispatch])

  const selectAllItems = useCallback(() => {
    const allItemIds = state.workflowItems.map(item => item.id!).filter(Boolean)
    dispatch({ type: 'SELECT_ALL_ITEMS', payload: allItemIds })
  }, [state.workflowItems, dispatch])

  const deselectAllItems = useCallback(() => {
    dispatch({ type: 'DESELECT_ALL_ITEMS' })
  }, [dispatch])

  const setCurrentPage = useCallback((page: number) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page })
  }, [dispatch])

  const setPageSize = useCallback((size: number) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: size })
  }, [dispatch])

  const updateVisibleColumns = useCallback((columns: FieldConfiguration[]) => {
    dispatch({ type: 'UPDATE_VISIBLE_COLUMNS', payload: columns })
  }, [dispatch])

  const removeWorkOrder = useCallback((orderId: string) => {
    dispatch({ type: 'REMOVE_WORK_ORDER', payload: orderId })
  }, [dispatch])

  const removeWorkOrdersBulk = useCallback((orderIds: string[]) => {
    orderIds.forEach(orderId => {
      dispatch({ type: 'REMOVE_WORK_ORDER', payload: orderId })
    })
  }, [dispatch])

  // Ref to store the latest state for the debounced function
  const stateRef = useRef(state)
  
  // Update the ref whenever state changes
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Debounced function to save view state when filters change
  const debouncedSaveViewState = useCallback(
    debounce(async () => {
      // Get the current state from the ref
      const currentState = stateRef.current

      if (!currentState.currentView?.viewSettingsId) {
        return;
      }

      try {
        // Build stage configurations from current state
        const stageConfigurations = currentState.stages.map(stage => ({
          stageConfigurationId: stage.stageId,
          fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
          pinnedColumnId: (currentState.pinnedColumnsByStage || {})[stage.stageId] || null,
        }))

        // Serialize stage filters with values as JSON strings
        const serializedStageFilters = currentState.stageFilters.map(filter => ({
          ...filter,
          values: filter.values ? JSON.stringify(filter.values) : undefined
        }))

        // Get all visible view IDs from the views response
        const visibleViewIds: string[] = [];
        
        // Add default view if it exists and is visible
        if (currentState.viewsResponse?.defaultView && currentState.viewsResponse.defaultView.isVisible !== false) {
          visibleViewIds.push(currentState.viewsResponse.defaultView.viewSettingsId);
        }
        
        // Add user views that are visible
        if (currentState.viewsResponse?.userViews) {
          currentState.viewsResponse.userViews.forEach(view => {
            if (view.isVisible !== false) {
              visibleViewIds.push(view.viewSettingsId);
            }
          });
        }
        
        // Add shared views that are visible
        if (currentState.viewsResponse?.sharedViews) {
          currentState.viewsResponse.sharedViews.forEach(view => {
            if (view.isVisible !== false) {
              visibleViewIds.push(view.viewSettingsId);
            }
          });
        }
        
        // Add public views that are visible
        if (currentState.viewsResponse?.publicViews) {
          currentState.viewsResponse.publicViews.forEach(view => {
            if (view.isVisible !== false) {
              visibleViewIds.push(view.viewSettingsId);
            }
          });
        }

        // Serialize columnWidths before sending to backend
        const serializedColumnWidths = JSON.stringify(currentState.columnWidths)

        const viewStateRequest = {
          id: currentState.currentView.viewSettingsId,
          title: currentState.currentView.title,
          isDefault: false,
          isPublic: currentState.currentView.isPublic ?? false,
          sharedUserIds: [],
          stageConfigurations,
          stageFilters: serializedStageFilters,
          globalFilters: currentState.globalFilters.map(filter => ({
            fieldConfigurationId: filter.fieldConfigurationId,
            filter: filter.filter,
            values: typeof filter.values === 'string' ? filter.values : (filter.values ? JSON.stringify(filter.values) : undefined)
          })),
          visibleViewIds: visibleViewIds,
          itemsPerPage: currentState.itemsPerPage,
          columnWidths: serializedColumnWidths,
          isUserViewStateUpdate: true
        }
        
        await workflowApi.saveViewState(viewStateRequest)
      } catch (error) {
        console.error('Failed to save view state:', error)
      }
    }, 1000), // 1 second debounce
    [workflowApi] // Only depend on workflowApi, state is accessed via ref
  )

  // Helper function to build visibleViewIds from viewsResponse
  const buildVisibleViewIds = useCallback((viewsResponse: UserViewsResponse | null | undefined): string[] => {
    if (!viewsResponse) return []
    
    const visibleViewIds: string[] = []
    
    // Add default view if visible (default view is always visible if it exists)
    if (viewsResponse.defaultView && viewsResponse.defaultView.isVisible !== false) {
      visibleViewIds.push(viewsResponse.defaultView.viewSettingsId)
    }
    
    // Add user views where isVisible is true
    if (viewsResponse.userViews) {
      viewsResponse.userViews.forEach(view => {
        if (view.isVisible === true) {
          visibleViewIds.push(view.viewSettingsId)
        }
      })
    }
    
    // Add shared views where isVisible is true
    if (viewsResponse.sharedViews) {
      viewsResponse.sharedViews.forEach(view => {
        if (view.isVisible === true) {
          visibleViewIds.push(view.viewSettingsId)
        }
      })
    }
    
    // Add public views where isVisible is true
    if (viewsResponse.publicViews) {
      viewsResponse.publicViews.forEach(view => {
        if (view.isVisible === true) {
          visibleViewIds.push(view.viewSettingsId)
        }
      })
    }
    
    return visibleViewIds
  }, [])

  // Save view state immediately without affecting loading or data
  const saveViewStateSilently = useCallback(async (updatedPinnedColumns?: Record<string, string | null>, viewsResponseOverride?: UserViewsResponse | null) => {
    const currentState = stateRef.current
    if (!currentState.currentView?.viewSettingsId) {
      return
    }
    try {
      // Use updated pinned columns if provided, otherwise use current state
      const pinnedColumns = updatedPinnedColumns || currentState.pinnedColumnsByStage || {}
      
      const stageConfigurations = currentState.stages.map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: pinnedColumns[stage.stageId] || null,
      }))
      const serializedStageFilters = currentState.stageFilters.map(filter => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined
      }))
      
      // Use provided viewsResponse if available (fresh from API), otherwise use current state
      const viewsResponseToUse = viewsResponseOverride !== undefined ? viewsResponseOverride : currentState.viewsResponse
      const visibleViewIds = buildVisibleViewIds(viewsResponseToUse)
      const viewStateRequest = {
        id: currentState.currentView.viewSettingsId,
        title: currentState.currentView.title,
        isDefault: false,
        isPublic: currentState.currentView.isPublic ?? false,
        sharedUserIds: [],
        stageConfigurations,
        stageFilters: serializedStageFilters,
        globalFilters: currentState.globalFilters.map(filter => ({
          fieldConfigurationId: filter.fieldConfigurationId,
          filter: filter.filter,
          values: typeof filter.values === 'string' ? filter.values : (filter.values ? JSON.stringify(filter.values) : undefined)
        })),
        visibleViewIds,
        itemsPerPage: currentState.itemsPerPage,
        columnWidths: JSON.stringify(currentState.columnWidths),
        isUserViewStateUpdate: true
      }
      await workflowApi.saveViewState(viewStateRequest)
    } catch (error) {
      console.error('Failed to silently save view state:', error)
    }
  }, [workflowApi, buildVisibleViewIds])

  // New operations
  const setStageFilters = useCallback((filters: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[]) => {
    // Check if Client filter is being changed in stage filters
    const clientField = state.configuration?.fieldsInUse.find(f => f.columnCode === 'Client')
    if (clientField && state.configuration && state.currentStage) {
      const currentStageConfig = state.stages.find(s => s.stageCode === state.currentStage)
      if (currentStageConfig) {
        const oldClientFilter = state.stageFilters.find(
          f => f.stageId === currentStageConfig.stageId && 
               f.fieldConfigurationId === clientField.fieldConfigurationId
        )
        const newClientFilter = filters.find(
          f => f.stageId === currentStageConfig.stageId && 
               f.fieldConfigurationId === clientField.fieldConfigurationId
        )
        
        // Compare filters to detect change (including existence check)
        const oldFilterStr = oldClientFilter?.filter || ''
        const newFilterStr = newClientFilter?.filter || ''
        
        // Also check if existence changed (filter added or removed)
        const oldExists = !!oldClientFilter
        const newExists = !!newClientFilter
        
        if (oldFilterStr !== newFilterStr || oldExists !== newExists) {
          // Client filter changed - set flag immediately before updating filters
          // Only set flag if we have the necessary context to handle it
          dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: true })
        }
      }
    }
    
    dispatch({ type: 'SET_STAGE_FILTERS', payload: filters })
    // Call view-state endpoint with updated filters
    debouncedSaveViewState()
  }, [dispatch, debouncedSaveViewState, state.configuration, state.currentStage, state.stages, state.stageFilters])

  const setGlobalFilters = useCallback((filters: { fieldConfigurationId: string; filter: string; values?: string }[]) => {
    // Check if Client filter is being changed
    const clientField = state.configuration?.fieldsInUse.find(f => f.columnCode === 'Client')
    
    if (clientField && state.configuration && state.currentStage) {
      const oldClientFilter = state.globalFilters.find(f => f.fieldConfigurationId === clientField.fieldConfigurationId)
      const newClientFilter = filters.find(f => f.fieldConfigurationId === clientField.fieldConfigurationId)
      
      // Compare filters to detect change (including existence check)
      const oldFilterStr = oldClientFilter?.filter || ''
      const newFilterStr = newClientFilter?.filter || ''
      
      // Also check if existence changed (filter added or removed)
      const oldExists = !!oldClientFilter
      const newExists = !!newClientFilter
      
      if (oldFilterStr !== newFilterStr || oldExists !== newExists) {
        // Client filter changed - set flag immediately before updating filters
        // Only set flag if we have the necessary context to handle it
        dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: true })
      }
    }
    
    dispatch({ type: 'SET_GLOBAL_FILTERS', payload: filters })
    // Call view-state endpoint with updated filters
    debouncedSaveViewState()
  }, [dispatch, debouncedSaveViewState, state.configuration, state.currentStage, state.globalFilters])

  const clearAllFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_FILTERS' })
    // Call view-state endpoint with cleared filters
    debouncedSaveViewState()
  }, [dispatch, debouncedSaveViewState])

  const setItemsPerPage = useCallback((itemsPerPage: number | null) => {
    dispatch({ type: 'SET_ITEMS_PER_PAGE', payload: itemsPerPage })
    // Call view-state endpoint to save the itemsPerPage setting
    debouncedSaveViewState();
    // Remove the manual refreshData call - let the grid's main effect handle it
    // The grid's useEffect will trigger when pageSize changes
  }, [dispatch, debouncedSaveViewState])

  // Function to manually set code values (for when window.PhoenixCodeValues is not available)
  const setCodeValues = useCallback((codeValuesData: any) => {
    if (typeof window !== "undefined") {
      // Set the global code values
      (window as any).PhoenixCodeValues = codeValuesData
      
      // Dispatch a custom event to notify the hook
      window.dispatchEvent(new CustomEvent('phoenixCodeValuesLoaded', { 
        detail: codeValuesData 
      }))
    }
  }, [])

  // Function to force fetch code values on initialization
  const refreshCodeValues = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING_CODE_VALUES', payload: true })
      
      // Fetch code values from the API using the existing codeService
      const codeValuesData = await codeService.getCodeValues(token)
      
      // Store in state
      dispatch({ type: 'SET_CODE_VALUES_DATA', payload: codeValuesData })
      
      // Also set in window for the hook
      setCodeValues(codeValuesData)
      
    } catch (error) {
      dispatch({ type: 'SET_CODE_VALUES_ERROR', payload: 'Failed to load code values' })
    }
  }, [setCodeValues, token])

  // Force fetch code values on initialization
  useEffect(() => {
    // Only fetch if we haven't loaded them yet and the hook is not ready
    if (!codeValues.isReady && !state.codeValuesData && !state.isLoadingCodeValues) {
      refreshCodeValues();
    }
  }, [codeValues.isReady, state.codeValuesData, state.isLoadingCodeValues, refreshCodeValues]);

  // Column widths operations
  const setColumnWidths = useCallback((columnWidths: Record<string, number>) => {
    dispatch({ type: 'SET_COLUMN_WIDTHS', payload: columnWidths })
    // Call view-state endpoint with updated column widths
    debouncedSaveViewState()
  }, [dispatch, debouncedSaveViewState])

  // Pinned column operation
  const setPinnedColumn = useCallback((columnId: string | null) => {
    const currentStageConfig = stateRef.current.stages.find(s => s.stageCode === stateRef.current.currentStage)
    const stageId = currentStageConfig?.stageId
    if (!stageId) return
    
    // Create updated pinned columns map
    const updatedPinnedColumns = {
      ...(stateRef.current.pinnedColumnsByStage || {}),
      [stageId]: columnId
    }
    
    dispatch({ type: 'SET_PINNED_COLUMN', payload: { stageId, columnId } })
    // No local persistence; configuration/view-state handles saving
    // Persist to view-state immediately with the updated pinned column info
    void saveViewStateSilently(updatedPinnedColumns)
  }, [saveViewStateSilently])

  // Delete view
  const deleteView = useCallback(async (viewId: string) => {
    try {
      await workflowApi.deleteViewSettings(viewId)
      
      // Refresh views after deletion
      const viewsResponse = await workflowApi.fetchViews()
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })
      
      // If the deleted view was the current view, switch to default view
      if (state.currentView?.viewSettingsId === viewId) {
        const defaultView = viewsResponse.defaultView || null
        dispatch({ type: 'SET_CURRENT_VIEW', payload: defaultView })
        // Refresh configuration with the default view
        if (defaultView) {
          refreshConfiguration(undefined, defaultView.viewSettingsId)
        } else {
          refreshConfiguration(undefined, null)
        }
      }
    } catch (error) {
      console.error('Failed to delete view:', error)
      throw error
    }
  }, [state.currentView, workflowApi, dispatch, refreshConfiguration])

  const contextValue: WorkflowContextType = {
    // State
    state,
    currentUser,
    currentStage,
    isFilterDrawerOpen,
    isBulkEditModalOpen,
    isLoadingColumns,
    savedViews,
    currentView,
    hasUnsavedChanges,
    filters,
    
    // View data from API
    viewsResponse: state.viewsResponse, // This will be populated by the useEffect that fetches views
    
    // Data properties for WorkflowGrid
    workOrders,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    columns,
    visibleColumns,
    selectedItems,
    currentPage,
    pageSize,
    
    // Loading states
    isLoadingWorkOrders,
    isLoadingRows,
    
    // Actions
    setCurrentStage,
    toggleFilterDrawer,
    updateFilter,
    clearFilters,
    setSelectedItems,
    toggleItemSelection,
    selectAllItems,
    deselectAllItems,
    setCurrentPage,
    setPageSize,
    setItemsPerPage,
    updateVisibleColumns,
    
    // Data operations
    refreshData,
    updateWorkOrderField,
    updateWorkOrderFieldOptimistically,
    removeWorkOrder,
    removeWorkOrdersBulk,
    bulkUpdateWorkOrderField,
    updateWorkOrdersOptimistically,
    processAction,
    bulkAssignWorkOrders,
    
    // Modal operations
    openBulkEditModal,
    closeBulkEditModal,
    
    // View operations
    setCurrentView,
    setSavedViews,
    setUnsavedChanges,
    saveCurrentView,
    saveViewAs,
    deleteView,
    updateViewMetadata,
    refreshViews,
    
    // Column operations
    addCustomColumn,
    deleteColumn,

    // Configuration operations
    refreshConfiguration,
    updateConfigurationDirectly,
    configuration,
    isLoadingConfiguration,
    configurationError,

    // Stages
    stages,

    // Assigned Users operations
    refreshAssignedUsers,
    assignedUsers,
    isLoadingAssignedUsers,
    assignedUsersError,

    // Facets
    facets,

    // New operations
    setStageFilters,
    setGlobalFilters,
    clearAllFilters,

    // New state for stageFilters and globalFilters
    stageFilters: state.stageFilters,
    globalFilters: state.globalFilters,

    // New state for work orders facets
    workOrdersFacets: state.workOrdersFacets,

    // Code Values
    codeValues,
    setCodeValues,
    
    // Code Values State
    codeValuesData: state.codeValuesData,
    isLoadingCodeValues: state.isLoadingCodeValues ?? false,
    codeValuesError: state.codeValuesError ?? null,
    refreshCodeValues,

    // Column Widths operations
    setColumnWidths,

    // Pinned column operations
    setPinnedColumn,

    // Save view state silently
    saveViewStateSilently,
  }

  // Effect to handle client filter changes and fetch custom fields
  const isFetchingCustomFields = useRef(false)
  const requestIdRef = useRef(0)
  
  useEffect(() => {
    // Only proceed if flag is set (meaning client filter changed)
    if (!state.isHandlingClientFilterChange) return
    
    // Configuration and stage should be available since we check before setting flag
    if (!state.configuration || !state.currentStage) return

    let shouldRefresh = false
    let shouldFetchCustomFields = false
    let shouldClearCustomFields = false
    let clientName: string | null = null

    // Immediately analyze the situation synchronously
    const clientField = state.configuration?.fieldsInUse.find(f => f.columnCode === 'Client')
    
    if (!clientField) {
      // No Client field, clear flag immediately and let grid handle it
      dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: false })
      return
    }

    // Helper to parse filter values from filter string
    const parseFilterValues = (filter: { filter: string; values?: string | any }): { values: string[]; hasBlank: boolean } => {
      try {
        if (filter.values) {
          // Check if values is already an object or needs parsing
          let metadata: any
          if (typeof filter.values === 'string') {
            metadata = JSON.parse(filter.values)
          } else {
            // Already parsed object
            metadata = filter.values
          }
          
          if (metadata.isSelectAll) return { values: [], hasBlank: false }
          if (metadata.regularValues) {
            return { 
              values: metadata.regularValues, 
              hasBlank: metadata.hasBlanks || false 
            }
          }
        }
      } catch (e) {
        // Fallback to regex parsing
        const matches = Array.from(filter.filter.matchAll(/eq '([^']+)'/g))
        const values = matches.map(m => m[1])
        // Check if filter includes blank condition
        const hasBlank = filter.filter.includes('eq null') || 
                        filter.filter.includes("eq ''") ||
                        filter.filter.includes('eq ""')
        return { values, hasBlank }
      }
      return { values: [], hasBlank: false }
    }

    // Check global filters
    const globalClientFilter = state.globalFilters.find(
      f => f.fieldConfigurationId === clientField.fieldConfigurationId
    )

    // Check stage filters for current stage
    const currentStageConfig = state.stages.find(s => s.stageCode === state.currentStage)
    const stageClientFilter = currentStageConfig
      ? state.stageFilters.find(
          f => f.stageId === currentStageConfig.stageId &&
               f.fieldConfigurationId === clientField.fieldConfigurationId
        )
      : null

    // Parse both filter values
    const globalParsed = globalClientFilter ? parseFilterValues(globalClientFilter) : null
    const stageParsed = stageClientFilter ? parseFilterValues(stageClientFilter) : null

    // Determine the effective single client selection
    let globalClient: string | null = null
    let stageClient: string | null = null

    if (globalParsed && globalParsed.values.length === 1 && globalParsed.values[0] !== '__BLANK__' && !globalParsed.hasBlank) {
      globalClient = globalParsed.values[0]
    }

    if (stageParsed && stageParsed.values.length === 1 && stageParsed.values[0] !== '__BLANK__' && !stageParsed.hasBlank) {
      stageClient = stageParsed.values[0]
    }

    // Decision logic:
    // 1. If both global and stage have different single clients → Clear custom fields and refresh
    // 2. If both global and stage have the SAME single client → Keep existing custom fields (no fetch if already applied)
    // 3. If only global has single client → Use that client
    // 4. If only stage has single client → Use that client
    // 5. If neither has single client or has blanks/multiple → Clear custom fields and refresh

    if (globalClient && stageClient) {
      if (globalClient === stageClient) {
        // Same client in both - keep/fetch for this client
        clientName = globalClient
        // Only fetch if not already applied for this client
        if (state.activeClientName !== clientName) {
          shouldFetchCustomFields = true
        }
        // Otherwise, keep existing custom fields, just refresh data
        else {
          shouldRefresh = true
        }
      } else {
        // Different clients - conflict, clear custom fields and refresh
        shouldClearCustomFields = true
        shouldRefresh = true
      }
    } else if (globalClient) {
      // Only global has single client
      clientName = globalClient
      if (state.activeClientName !== clientName) {
        shouldFetchCustomFields = true
      } else {
        shouldRefresh = true
      }
    } else if (stageClient) {
      // Only stage has single client
      clientName = stageClient
      if (state.activeClientName !== clientName) {
        shouldFetchCustomFields = true
      } else {
        shouldRefresh = true
      }
    } else {
      // No single client (multiple, blank, or none)
      shouldClearCustomFields = true
      shouldRefresh = true
    }

    // Clear custom fields if needed
    if (shouldClearCustomFields && state.customFieldsApplied) {
      dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
    }

    // Now handle the async part
    async function handleClientFilterChange() {
      try {
        // If we cleared custom fields, we need to refresh data with the reverted configuration
        if (shouldClearCustomFields && shouldRefresh) {
          const currentStageConfig = state.stages.find(s => s.stageCode === state.currentStage)
          if (currentStageConfig) {
            const currentStageFilters = state.stageFilters.filter(f => f.stageId === currentStageConfig.stageId)
            const allFilters = [...state.globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean)
            const baseFilter = `Stage eq '${state.currentStage}'`
            const filterArray = allFilters.length > 0 
              ? [`${baseFilter} and ${allFilters.join(' and ')}`]
              : [baseFilter]
            
            const facetKeys = getFacetKeysFromConfiguration(state.configuration)
            await refreshData(filterArray, undefined, facetKeys)
          }
          return
        }
        
        // If we just need to refresh (no custom fields to fetch), don't call refreshData here
        // The grid's useEffect will handle the refresh automatically when stage/filters change
        if (shouldRefresh && !shouldFetchCustomFields) {
          // Just clear the flag and let the grid handle the refresh
          return
        }

        if (shouldFetchCustomFields && clientName) {
          // Increment request ID to track this specific request
          requestIdRef.current += 1
          const currentRequestId = requestIdRef.current

          // Prevent concurrent fetches
          if (isFetchingCustomFields.current) {
            return
          }

          isFetchingCustomFields.current = true

          try {
            const searchFilter = `Client/Name eq '${clientName.replace(/'/g, "''")}' and FboId ne null and IsDeleted eq false`
            const response = await workflowApi.searchWorkflowItems(
              undefined,
              undefined,
              [],
              1,
              1,
              '*',
              'FboId,Client',
              searchFilter,
              undefined,
              []
            )

            if (response.data.length === 0) {
              console.warn('Client not found with FboId')
              if (state.customFieldsApplied) {
                dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
              }
              // Don't call refreshData here - the grid's useEffect will handle it automatically
              return
            }

            const clientData = response.data[0].client || response.data[0].Client
            if (!clientData) {
              console.warn('Client data not found in response')
              if (state.customFieldsApplied) {
                dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
              }
              // Don't call refreshData here - the grid's useEffect will handle it automatically
              return
            }

            const clientFboId = clientData.fboId || clientData.FboId
            if (!clientFboId) {
              console.warn('Client found but no FboId')
              if (state.customFieldsApplied) {
                dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
              }
              // Don't call refreshData here - the grid's useEffect will handle it automatically
              return
            }

            // Check cache
            const cachedFields = state.cachedClientCustomFields.get(clientFboId)
            if (cachedFields) {
              // Only apply if this is still the latest request
              if (currentRequestId !== requestIdRef.current) return
              
              dispatch({
                type: 'APPLY_CUSTOM_FIELDS',
                payload: { customFields: cachedFields, clientFboId, clientName }
              })
              const currentStageConfig = state.stages.find(s => s.stageCode === state.currentStage)
              if (currentStageConfig) {
                const currentStageFilters = state.stageFilters.filter(f => f.stageId === currentStageConfig.stageId)
                const allFilters = [...state.globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean)
                const baseFilter = `Stage eq '${state.currentStage}'`
                const filterArray = allFilters.length > 0 
                  ? [`${baseFilter} and ${allFilters.join(' and ')}`]
                  : [baseFilter]
                
                const facetKeys = getFacetKeysFromConfiguration(state.configuration)
                await refreshData(filterArray, undefined, facetKeys)
              }
              return
            }

            // Fetch custom fields
            const customFieldsResponse = await workflowApi.fetchClientCustomFields(
              clientFboId,
              FboEntityType.WorkOrder
            )

            // Only apply if this is still the latest request (prevent race conditions)
            if (currentRequestId !== requestIdRef.current) return

            if (customFieldsResponse && customFieldsResponse.CustomFields.length > 0) {
              // Client has custom fields - cache and apply them
              dispatch({
                type: 'CACHE_CLIENT_CUSTOM_FIELDS',
                payload: { clientFboId, customFields: customFieldsResponse.CustomFields }
              })
              dispatch({
                type: 'APPLY_CUSTOM_FIELDS',
                payload: { customFields: customFieldsResponse.CustomFields, clientFboId, clientName }
              })
            } else {
              // Client has no custom fields - clear any previously applied ones
              if (state.customFieldsApplied) {
                dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
              }
              // Still cache the empty result to avoid refetching
              dispatch({
                type: 'CACHE_CLIENT_CUSTOM_FIELDS',
                payload: { clientFboId, customFields: [] }
              })
            }

            // Always refresh after custom fields
            const currentStageConfig = state.stages.find(s => s.stageCode === state.currentStage)
            if (currentStageConfig) {
              const currentStageFilters = state.stageFilters.filter(f => f.stageId === currentStageConfig.stageId)
              const allFilters = [...state.globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean)
              const baseFilter = `Stage eq '${state.currentStage}'`
              const filterArray = allFilters.length > 0 
                ? [`${baseFilter} and ${allFilters.join(' and ')}`]
                : [baseFilter]
              
              const facetKeys = getFacetKeysFromConfiguration(state.configuration)
              await refreshData(filterArray, undefined, facetKeys)
            }
          } finally {
            isFetchingCustomFields.current = false
          }
        }
      } catch (error) {
        console.error('Failed to handle client filter change:', error)
        if (state.customFieldsApplied) {
          dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
        }
        // Don't call refreshData here - the grid's useEffect will handle it automatically
      } finally {
        // Always clear the flag
        dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: false })
      }
    }

    handleClientFilterChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isHandlingClientFilterChange])

  // Effect to check for client-specific custom fields on initial load
  const hasCheckedInitialClient = useRef(false)
  
  useEffect(() => {
    // Only run once after configuration and filters are loaded
    if (hasCheckedInitialClient.current) return
    if (!state.configuration || !state.currentStage) return
    
    // Wait a tick to ensure filters are loaded from view configuration
    const timeoutId = setTimeout(() => {
      if (hasCheckedInitialClient.current) return
      hasCheckedInitialClient.current = true
      
      // Trigger the client filter change check
      dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: true })
    }, 100)
    
    return () => clearTimeout(timeoutId)
  }, [state.configuration, state.currentStage, dispatch])

  return (
    <WorkflowContext.Provider value={contextValue}>
      {children}
    </WorkflowContext.Provider>
  )
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined)

export function useWorkflow() {
  const context = useContext(WorkflowContext)
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider')
  }
  return context
} 
